/**
 * Apply an AI-generated Query to the UDE form.
 *
 * Resets existing selections first, then sets measurement, metrics, dimensions,
 * rollups, filters, time, viz, and sort from the query. Uses addMetricToForm /
 * addDimensionToForm so rollup and window.fn defaults are applied correctly.
 */
import type { Query, Measurement, WindowFunction } from '@kentik/ui-shared/query/query.types';
import type { UdeFormProps } from '../form.types';
import { addMetricToForm, addDimensionToForm } from '../selectionHandlers';

type UdeForm = UdeFormProps['form'];

export function applyNlqQuery(form: UdeForm, query: Query, measurements: Measurement[]): void {
  // 1. Set measurement first (addMetricToForm reads it from the form)
  form.setValue('config.data.measurement', query.measurement);

  // 2. Reset existing selections
  form.setValue('config.data.metrics', []);
  form.setValue('config.data.dimensions', []);
  form.setValue('config.data.rollups', []);
  form.setValue('config.data.sort', []);
  form.setValue('config.data.window.fn', {});

  // 3. Add metrics via addMetricToForm (handles rollups + window.fn automatically)
  for (const metric of query.metrics) {
    addMetricToForm(form, metric.name, measurements);
  }

  // 4. Add dimensions
  for (const dimension of query.dimensions) {
    addDimensionToForm(form, dimension.name);
  }

  // 5. Set time range
  if (query.time) {
    form.setValue('config.data.time', query.time);
  }

  // 6. Set filters via getField (filterGroups is a collection field, not a plain value)
  if (query.filters?.dimensions) {
    const { connector, filterGroups = [] } = query.filters.dimensions;
    const dimFilterGroupsField = form.getField('config.data.filters.dimensions.filterGroups');
    if (dimFilterGroupsField?.setValue) {
      dimFilterGroupsField.setValue(filterGroups);
    }
    if (connector) {
      const dimConnector = form.getField('config.data.filters.dimensions.connector');
      if (dimConnector?.setValue) {
        (dimConnector.setValue as (v: string) => void)(connector);
      }
    }
  }
  if (query.filters?.sources) {
    const { connector, filterGroups = [] } = query.filters.sources;
    const srcFilterGroupsField = form.getField('config.data.filters.sources.filterGroups');
    if (srcFilterGroupsField?.setValue) {
      srcFilterGroupsField.setValue(filterGroups);
    }
    if (connector) {
      const srcConnector = form.getField('config.data.filters.sources.connector');
      if (srcConnector?.setValue) {
        (srcConnector.setValue as (v: string) => void)(connector);
      }
    }
  }

  // 7. Set viz/chart type
  if (query.viz?.type) {
    form.setValue('config.chart.type', query.viz.type);
  }

  // 8. Set sort (ensure order is always specified)
  if (query.sort) {
    form.setValue(
      'config.data.sort',
      query.sort.map((s) => ({ rollup: s.rollup, order: s.order ?? 'desc' }))
    );
  }

  // 9. Override rollups if LLM specified non-default functions
  // Do this after addMetricToForm so the LLM's explicit rollups take precedence
  if (query.rollups?.length) {
    form.setValue('config.data.rollups', query.rollups);

    // Re-sync window.fn to match the overridden rollups
    const windowFn: Record<string, WindowFunction> = {};
    for (const rollup of query.rollups) {
      const sqlFn: WindowFunction = (['min', 'max', 'avg', 'sum'] as const).includes(
        rollup.fn as 'min' | 'max' | 'avg' | 'sum'
      )
        ? (rollup.fn as WindowFunction)
        : 'avg';
      windowFn[rollup.metric] = sqlFn;
    }
    form.setValue('config.data.window.fn', windowFn);
  }
}
